"use client";
import { EventName } from "@mw/shared/analyticsEventNames";
import { toast } from "@/app/_providers/toast";
import { useAnalytics } from "@ui/hooks/useAnalytics";
import Copy16 from "@ui/icons/16x16/copy-16.svg";
import Share16 from "@ui/icons/16x16/share-16.svg";
import Facebook24 from "@ui/icons/24x24/facebook-24.svg";
import Linkedin24 from "@ui/icons/24x24/linkedin-24.svg";
import X24 from "@ui/icons/24x24/x-24.svg";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { Button } from "./Button";
import { DropdownMenuContent } from "./DropdownMenu";
import { DropdownMenuTrigger } from "./DropdownMenu";
import { DropdownMenu } from "./DropdownMenu";
import { Link } from "./Link";

interface ClickShareOption {
  ariaLabel: string;
  href?: undefined;
  icon: React.ReactNode;
  label: string;
  onClick: () => Promise<void>;
}

interface LinkShareOption {
  ariaLabel: string;
  href: string;
  icon: React.ReactNode;
  label: string;
  onClick?: undefined;
  platform: string;
  target: string;
}

export type ShareOption = ClickShareOption | LinkShareOption;

interface SocialShareButtonProps {
  asDropdown?: boolean;
  beforeShare?: () => Promise<void>;
  className?: string;
  customTrigger?: React.ReactNode;
  extraShareOptions?: ShareOption[];
  label: string;
  url: string;
  withIcon?: boolean;
}

export const SocialShareButton = ({
  label,
  url,
  className,
  withIcon = true,
  asDropdown = true,
  customTrigger,
  beforeShare,
  extraShareOptions = [],
}: SocialShareButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations("ClientKeys.Globals");
  const analytics = useAnalytics();

  const encodedUrl = encodeURIComponent(url);

  const shareOptions: ShareOption[] = [
    {
      label: t("share.copyLink"),
      onClick: async () => {
        if (beforeShare) {
          await beforeShare();
        }
        await navigator.clipboard.writeText(url);
        setIsOpen(false);
        toast.success(t("share.copyLinkSuccess"));
        analytics.captureEvent(EventName.website_clicked_share_option, {
          option: "copy_link",
          url,
        });
      },
      icon: <Copy16 />,
      ariaLabel: t("share.copyLink"),
    },
    {
      label: t("share.twitter"),
      href: `https://x.com/intent/tweet?url=${encodedUrl}`,
      target: "_blank",
      platform: "x",
      icon: <X24 />,
      ariaLabel: t("share.twitter"),
    },
    {
      label: t("share.linkedin"),
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      target: "_blank",
      platform: "linkedin",
      icon: <Linkedin24 />,
      ariaLabel: t("share.linkedin"),
    },
    {
      label: t("share.facebook"),
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      target: "_blank",
      platform: "facebook",
      icon: <Facebook24 />,
      ariaLabel: t("share.facebook"),
    },
    ...extraShareOptions.map(option => {
      return {
        ...option,
        onClick: option.onClick
          ? async () => {
              if (beforeShare) {
                await beforeShare();
              }
              await option.onClick();
              setIsOpen(false);
            }
          : undefined,
      } as ShareOption;
    }),
  ];

  useEffect(() => {
    if (isOpen) {
      analytics.captureEvent(EventName.website_clicked_share_button, {
        url,
      });
    }
  }, [isOpen, url, analytics]);

  const handleShareOptionClick = async (platform: string) => {
    if (beforeShare) {
      await beforeShare();
    }
    analytics.captureEvent(EventName.website_clicked_share_option, {
      option: platform,
      url,
    });
  };

  return asDropdown ? (
    <DropdownMenu onOpenChange={setIsOpen} open={isOpen}>
      <DropdownMenuTrigger asChild>
        {customTrigger ? (
          customTrigger
        ) : (
          <Button
            className={className}
            icon={withIcon ? <Share16 /> : undefined}
            iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
            variant='secondary'
          >
            {label}
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='start'
        className='shadow-blur rounded-md border-none bg-default p-16'
        side='bottom'
        sideOffset={10}
      >
        <div className='grid grid-cols-1 gap-12 text-sm'>
          {shareOptions.map(option => {
            return option.href ? (
              <Link
                className='w-full justify-start'
                href={option.href}
                key={option.label}
                onClick={() => handleShareOptionClick(option.platform)}
                target={option.target}
              >
                {option.label}
              </Link>
            ) : (
              <Button
                className='h-auto w-full justify-start p-0'
                key={option.label}
                onClick={option.onClick}
                size='sm'
                variant='ghost'
              >
                {option.label}
              </Button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : (
    <div className='flex items-center gap-8'>
      {shareOptions.map(option => {
        return (
          <Link
            aria-label={option.ariaLabel}
            asButton
            buttonVariants={{
              variant: "elevated",
              size: "pill",
            }}
            className='border-none bg-dark'
            href={option.href ?? url}
            icon={option.icon}
            key={option.label}
            onClick={e => {
              if (option.href) {
                analytics.captureEvent(EventName.website_clicked_share_option, {
                  option: option.platform,
                  url,
                });
              } else {
                e.preventDefault();
                e.stopPropagation();
              }
              option?.onClick?.();
            }}
            target='_blank'
          />
        );
      })}
    </div>
  );
};
